// =============================================================================
// src/chrome/copilot-sidebar/tactics-controller.ts
// Renderiza as "Oportunidades táticas — Específicas desta vista" por secao.
//
// Proveniencia: porte FIEL de assets/unified-app.js linhas 2684-2720
//   (CopilotTactics.wrap/list/counterEl/init/render). No original, init()
//   fazia monkey-patch de SectionManager.activateSection; aqui assinamos
//   'section:change' no AppBus (mesmo efeito, acoplamento tipado).
//
// O contador #copilotTacticsCount mostra baseTacticsCount (3) + nº de cards
//   especificos da vista, exatamente como o original (render, 2717-2719).
// =============================================================================

import type { AppContext, SectionId } from '../../app/types';
import { buildTacticCardMarkup } from './tactic-card-markup';
import {
  BASE_TACTICS_COUNT,
  TACTIC_CARDS_BY_SECTION,
} from './tactic-cards-by-section';
import {
  liveCampaignPriorities,
  liveTacticsForSection,
  setTacticsLiveListener,
} from './tactics-live-store';

/**
 * Controla o bloco de táticas especificas da vista (CopilotTactics). Esconde o
 *   wrap quando a secao nao tem táticas e atualiza o contador.
 */
export class CopilotTacticsController {
  private specificWrap: HTMLElement | null = null;
  private specificList: HTMLElement | null = null;
  private counterEl: HTMLElement | null = null;
  private priorityEl: HTMLElement | null = null;
  private currentSection: SectionId = 'mapa-eleitoral';

  /**
   * Coleta o DOM e assina 'section:change'. Renderiza a secao inicial fornecida
   *   (porte de init() — original usava SectionManager.activeSection || default).
   */
  init(ctx: AppContext, initialSection: SectionId): void {
    this.specificWrap = document.getElementById('copilotTacticsSpecificWrap');
    this.specificList = document.getElementById('copilotTacticsSpecific');
    this.counterEl = document.getElementById('copilotTacticsCount');
    this.priorityEl = document.getElementById('copilotTacticsPriority');
    if (!this.specificWrap || !this.specificList) return;
    ctx.bus.on('section:change', ({ section }) => this.render(section));
    // Re-renderiza prioridades + a seção corrente quando o override ao vivo chega após o init (fetch async).
    setTacticsLiveListener(() => this.applyLive());
    this.bindTacticActivation(ctx);
    this.renderPriorities();
    this.render(initialSection);
  }

  /** Aplica o override ao vivo (chega async): prioridades da campanha + táticas da seção corrente. */
  private applyLive(): void {
    this.renderPriorities();
    this.render(this.currentSection);
  }

  /** Troca os 3 cards estáticos "Prioritárias da campanha" pelos de IA quando houver override. */
  private renderPriorities(): void {
    const cards = liveCampaignPriorities();
    if (!this.priorityEl || !cards) return; // sem container ou sem override → mantém o mock estático
    this.priorityEl.innerHTML = cards.map(buildTacticCardMarkup).join('');
  }

  /**
   * "Ativar tática"/"Ver detalhes" abrem o CHAT REAL com o plano da tática.
   * Reforma 2026-06 (Fase 4): os CTAs eram mortos (nenhum listener) — a
   * proposta central do produto terminava em nada. Listener DELEGADO no
   * <aside> cobre os 3 cards estaticos prioritarios E os dinamicos por vista.
   */
  private bindTacticActivation(ctx: AppContext): void {
    const sidebar = document.getElementById('aiCopilot');
    if (!sidebar) return;
    sidebar.addEventListener('click', (event) => {
      const cta = (event.target as HTMLElement).closest<HTMLElement>('.copilot-tactic-cta');
      if (!cta) return;
      const card = cta.closest<HTMLElement>('.copilot-tactic-card');
      const title = card?.querySelector('.copilot-tactic-desc')?.textContent?.trim() ?? '';
      ctx.commands.openCopilotChat(`Ativar tática: ${title} — monte o plano de execução`);
    });
  }

  /**
   * Renderiza os cards da secao. Sem táticas → wrap.is-empty + lista vazia
   *   (porte de render(), 2705-2719).
   */
  private render(sectionId: SectionId): void {
    this.currentSection = sectionId;
    /* v8 ignore next -- render só roda após init confirmar wrap+list; guarda defensiva CLAUDE.md rule 11 */
    if (!this.specificList || !this.specificWrap) return;
    // Override ao vivo (gold.ai_action_recommendation tactical) com fallback ao mock estático.
    const cards = liveTacticsForSection(sectionId) ?? TACTIC_CARDS_BY_SECTION[sectionId] ?? [];
    if (!cards.length) {
      this.specificWrap.classList.add('is-empty');
      this.specificList.innerHTML = '';
    } else {
      this.specificWrap.classList.remove('is-empty');
      this.specificList.innerHTML = cards.map(buildTacticCardMarkup).join('');
    }
    this.updateCounter(cards.length);
  }

  /** Contador = nº de prioritárias (ao vivo ou base 3) + nº de específicas da vista. */
  private updateCounter(specificCount: number): void {
    if (!this.counterEl) return;
    const priorityCount = liveCampaignPriorities()?.length ?? BASE_TACTICS_COUNT;
    this.counterEl.textContent = String(priorityCount + specificCount);
  }
}
